import {
  CreateGUFStartRegistrationTask,
  EditGUFStartRegistrationTask,
  QualityRegimeOptions,
} from "@nens/brostar/uploadapi-client";

import { BrostarUiSchema } from "@/libs/brostar-form";

import { createGufUiSchemaLibrary } from "./guf-ui-schema-library";

/**
 * UI Schema hook for GUF StartRegistration forms
 */
export function useGufStartRegistrationUiSchema(
  qualityRegimeValue: QualityRegimeOptions | undefined
): BrostarUiSchema<CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask> {
  const {
    projectNumber,
    qualityRegime,
    deliveryAccountableParty,
    broId,
    correctionReason,
    objectIdAccountableParty,
    deliveryContext,
    startTime,
    identificationLicence,
    legalType,
    primaryUsageType,
    secondaryUsageTypes,
    humanConsumption,
    licensedInOut,
    maximumPerHour,
    maximumPerDay,
    maximumPerMonth,
    maximumPerQuarter,
    maximumPerYear,
    designInstallationId,
    designInstallationPos,
    installationFunction,
    wellPos,
    energyCharacteristics,
    energyCold,
    energyWarm,
    maximumInfiltrationTemperatureWarm,
    averageInfiltrationTemperatureCold,
    averageInfiltrationTemperatureWarm,
    powerCold,
    powerWarm,
    power,
    averageColdWater,
    averageWarmWater,
    maximumYearQuantityCold,
    maximumYearQuantityWarm,
    designWellId,
    wellFunctions,
    height,
    geometryPubliclyAvailable,
    maximumWellDepth,
    maximumWellDepthPubliclyAvailable,
    maximumWellCapacity,
    relativeTemperature,
    screenType,
    designScreenTop,
    designScreenBottom,
    designScreenGeometry,
    designLoopId,
    designLoopPos,
    loopType,
    startDate,
    endDate,
    geometryType,
    designSurfaceInfiltrationId,
    designSurfaceInfiltrationPos,
  } = createGufUiSchemaLibrary(qualityRegimeValue);

  const uiSchema: BrostarUiSchema<CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask> = {
    "ui:order": ["metadata", "sourcedocumentData"],
    metadata: {
      "ui:title": "Metadata",
      projectNumber,
      qualityRegime,
      deliveryAccountableParty,
      broId,
      correctionReason,
    },
    sourcedocumentData: {
      "ui:title": "Brondocumentgegevens",
      "ui:order": [
        "objectIdAccountableParty",
        "deliveryContext",
        "startTime",
        "identificationLicence",
        "legalType",
        "primaryUsageType",
        "secondaryUsageTypes",
        "humanConsumption",
        "licensedQuantities",
        "designInstallations",
      ],
      objectIdAccountableParty,
      deliveryContext,
      startTime,
      identificationLicence,
      legalType,
      primaryUsageType,
      secondaryUsageTypes,
      humanConsumption,
      licensedQuantities: {
        "ui:options": { labelField: "licensedInOut" },
        items: {
          "ui:order": [
            "licensedInOut",
            "maximumPerHour",
            "maximumPerDay",
            "maximumPerMonth",
            "maximumPerQuarter",
            "maximumPerYear",
          ],
          licensedInOut,
          maximumPerHour,
          maximumPerDay,
          maximumPerMonth,
          maximumPerQuarter,
          maximumPerYear,
        },
      },
      designInstallations: {
        "ui:options": { labelField: "designInstallationId" },
        items: {
          "ui:order": [
            "designInstallationId",
            "installationFunction",
            "designInstallationPos",
            "energyCharacteristics",
            "licensedQuantities",
            "designLoops",
            "designSurfaceInfiltrations",
            "designWells",
          ],
          designInstallationId,
          installationFunction,
          designInstallationPos,
          energyCharacteristics: {
            ...energyCharacteristics,
            "ui:order": [
              "energyCold",
              "energyWarm",
              "maximumInfiltrationTemperatureWarm",
              "averageInfiltrationTemperatureCold",
              "averageInfiltrationTemperatureWarm",
              "powerCold",
              "powerWarm",
              "power",
              "averageColdWater",
              "averageWarmWater",
              "maximumYearQuantityCold",
              "maximumYearQuantityWarm",
            ],
            energyCold,
            energyWarm,
            maximumInfiltrationTemperatureWarm,
            averageInfiltrationTemperatureCold,
            averageInfiltrationTemperatureWarm,
            powerCold,
            powerWarm,
            power,
            averageColdWater,
            averageWarmWater,
            maximumYearQuantityCold,
            maximumYearQuantityWarm,
          },
          licensedQuantities: {
            "ui:options": { labelField: "licensedInOut" },
            items: {
              "ui:order": [
                "licensedInOut",
                "maximumPerHour",
                "maximumPerDay",
                "maximumPerMonth",
                "maximumPerQuarter",
                "maximumPerYear",
              ],
              licensedInOut,
              maximumPerHour,
              maximumPerDay,
              maximumPerMonth,
              maximumPerQuarter,
              maximumPerYear,
            },
          },
          designLoops: {
            "ui:options": { labelField: "designLoopId" },
            items: {
              "ui:order": [
                "designLoopId",
                "designLoopPos",
                "loopType",
                "startDate",
                "endDate",
                "geometryType",
              ],
              designLoopId,
              designLoopPos,
              loopType,
              startDate,
              endDate,
              geometryType,
            },
          },
          designSurfaceInfiltrations: {
            "ui:options": { labelField: "designSurfaceInfiltrationId" },
            items: {
              "ui:order": ["designSurfaceInfiltrationId", "designSurfaceInfiltrationPos"],
              designSurfaceInfiltrationId,
              designSurfaceInfiltrationPos,
            },
          },
          designWells: {
            "ui:options": { labelField: "designWellId" },
            items: {
              "ui:order": [
                "designWellId",
                "wellFunctions",
                "installationFunction",
                "height",
                "wellPos",
                "geometryPubliclyAvailable",
                "maximumWellDepth",
                "maximumWellDepthPubliclyAvailable",
                "maximumWellCapacity",
                "relativeTemperature",
                "installationFunction",
                "designScreen",
              ],
              designWellId,
              installationFunction,
              wellFunctions, // Is possibly an array of options import many to many relationships, so no enum here
              height,
              wellPos,
              geometryPubliclyAvailable,
              maximumWellDepth,
              maximumWellDepthPubliclyAvailable,
              maximumWellCapacity,
              relativeTemperature,
              designScreen: {
                "ui:order": [
                  "screenType",
                  "designScreenTop",
                  "designScreenBottom",
                  "designScreenGeometry",
                  "geometryPubliclyAvailable",
                ],
                screenType,
                designScreenTop,
                designScreenBottom,
                designScreenGeometry,
                geometryPubliclyAvailable,
              },
            },
          },
        },
      },
    },
  };

  return uiSchema;
}
